import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { handleErrorToast } from 'src/components/molecules/Toast';
import { getSocket } from 'src/creators/User.creators';
import { getSocket as getOprSocket } from 'src/creators/OpenReporting.creators';
import { State } from 'src/types';
import { getRandomId } from 'src/utils/commonFunctions';

interface UseSocketSendMessageOptions {
  context?: 'reshub' | 'opr';
}

export const useSocketSendMessage = ({
  context = 'reshub',
}: UseSocketSendMessageOptions = {}) => {
  const reshubSession = useSelector(
    (state: State) => state.globalUsers.session,
  );
  const oprSession = useSelector(
    (state: State) => state.openReporting?.openReportingReducer?.session,
  );
  const session = context === 'opr' ? oprSession : reshubSession;

  const sendMessage = useCallback(
    (
      channel: string,
      value: string,
      onSuccess?: () => void,
      onError?: () => void,
    ): boolean => {
      if (!session) {
        return false;
      }

      const socket =
        context === 'opr' ? getOprSocket(session) : getSocket(session);
      socket
        .timeout(10000)
        .emit(
          'chat',
          { token: session, channel, value, id: getRandomId() },
          (err: Error | null) => {
            if (err) {
              handleErrorToast({
                toastMessage: 'Oops, something went wrong. Please try again.',
              });
              onError?.();
            } else {
              onSuccess?.();
            }
          },
        );
      return true;
    },
    [session, context],
  );

  const markMessagesAsRead = useCallback(
    (channel: string): boolean => {
      if (!session) {
        return false;
      }

      const socket =
        context === 'opr' ? getOprSocket(session) : getSocket(session);
      socket.emit('chat-read-messages', {
        token: session,
        channel,
      });
      return true;
    },
    [session, context],
  );

  return { sendMessage, markMessagesAsRead };
};
